import { useState, useCallback, useEffect } from 'react'
import Cropper from 'react-easy-crop'
import { Slider } from '@/components/ui/slider'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { ZoomIn, ZoomOut } from 'lucide-react'

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => reject(error))
    image.src = url
  })

async function getCroppedImg(imageSrc, pixelCrop, outputSize) {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  // Set canvas size to desired output size
  canvas.width = outputSize.width
  canvas.height = outputSize.height

  // Draw the cropped image scaled to output size
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outputSize.width,
    outputSize.height
  )

  // Return as blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob)
    }, 'image/jpeg', 0.95)
  })
}

export default function ImageCropper({
  open,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 1,
  shape = 'round', // 'round' or 'rect'
  outputSize = { width: 200, height: 200 },
  title = 'Crop Image'
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  // Revoke de blob-URL zodra we deze cropper-instantie kwijtraken (unmount) of
  // een nieuwe imageSrc krijgen. Anders lekt elke geopende crop een Blob.
  useEffect(() => {
    return () => {
      if (imageSrc?.startsWith?.('blob:')) {
        try { URL.revokeObjectURL(imageSrc) } catch {}
      }
    }
  }, [imageSrc])

  const onCropChange = (crop) => {
    setCrop(crop)
  }

  const onZoomChange = (zoom) => {
    setZoom(zoom)
  }

  const onCropAreaChange = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleCrop = async () => {
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, outputSize)
      // Await de upload zodat de dialog pas sluit als de call écht klaar is —
      // anders zou een falende upload een alert firen uit ge-unmounte state.
      await onCropComplete(croppedImage)
      onClose()
    } catch (e) {
      console.error('Error cropping image:', e)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card border-neon">
        <DialogHeader>
          <DialogTitle className="text-neon-green">{title}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Output size: {outputSize.width}x{outputSize.height}px
          </p>
        </DialogHeader>

        <div className="relative h-96 bg-black/50 rounded-md">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={shape}
            showGrid={false}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropAreaChange}
          />
        </div>

        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <ZoomOut className="h-4 w-4 text-muted-foreground" />
            <Slider
              value={[zoom]}
              onValueChange={(value) => setZoom(value[0])}
              min={1}
              max={3}
              step={0.1}
              className="flex-1"
            />
            <ZoomIn className="h-4 w-4 text-muted-foreground" />
          </div>

          <div className="text-xs text-muted-foreground text-center">
            Sleep de afbeelding om te positioneren • Gebruik de slider om te zoomen
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuleren
          </Button>
          <Button onClick={handleCrop} className="bg-neon-green text-background hover:bg-neon-green/90">
            Toepassen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
